import { useEffect, useMemo, useRef, useState } from "react";
import styles from "./styles.module.scss";
import { FC, Props } from "./typings";

import { faCaretDown, faCaretUp } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import tagsService from "api/tagsService";
import LabelIcon from "assets/icons/label-new.svg?react";
import NewInput from "components/NewInput";
import { ITag, TagType } from "dto/ITag";
import { useOnClickOutside } from "usehooks-ts";
import DropdownLogic from "components/DropdownLogic";
import { ENotificationType, notification } from "utils/notification";
import EditIcon from "assets/icons/mode_edit_24px.svg?react";
import HollowButton from "components/HollowButton";
import Button from "components/Button";
import classNames from "classnames";
import TagBadge from "components/TagBadge";
import { useTranslation } from "react-i18next";

const SearchLabel: FC<Props> = ({
  label,
  onChoose,
  type,
  selectedTagId,
  allowSearch = true,
  showButtons = false,
  showCounter = false,
}) => {
  const { t } = useTranslation();

  const { data: tagsData, refetch } = tagsService.useGetTags(
    false,
    type ?? TagType.Other,
    undefined
  );

  const { mutate: createTag } = tagsService.useCreateTag();
  const { mutate: editTag } = tagsService.useEditTag();

  const allTags = useMemo(() => tagsData?.data ?? [], [tagsData]);

  const [search, setSearch] = useState<ITag[]>([]);
  const [value, setValue] = useState("");

  const [internalSelected, setInternalSelected] = useState<ITag | null>(null);
  const [edit, setEdit] = useState<ITag | null>(null);
  const [active, setActive] = useState(false);

  const outsideRef = useRef(null);

  useOnClickOutside(outsideRef, () => {
    if (active) {
      setActive(false);
      setValue("");
      setEdit(null);
    }
  });

  useEffect(() => {
    if (!value.length) {
      setSearch(allTags);
      return;
    }

    setSearch(
      allTags.filter((tag: ITag) =>
        tag.Name.toLocaleLowerCase().includes(value.toLocaleLowerCase())
      )
    );
  }, [allTags, value]);

  const handleEditTag = () => {
    editTag(
      {
        Id: edit.Id,
        Name: value,
      },
      {
        onSuccess: () => {
          refetch();
          setEdit(null);
          setValue("");
        },
      }
    );
  };

  const handleCreateTag = () => {
    createTag(
      {
        Name: value,
        Type: type,
      },
      {
        onSuccess: (response) => {
          setValue("");
          setActive(false);
          onChoose?.(response.data);
        },
        onError: (err) => {
          notification({
            type: ENotificationType.ERROR,
            text: err.response.data.errorCodes[0].message,
            title: t("errors.occurred"),
          });
        },
      }
    );
  };

  const handleCheck = (e) => {
    if (e.key === "Enter" && active) {
      if (edit) {
        handleEditTag();
        return;
      }
      handleCreateTag();
    }
  };

  useEffect(() => {
    if (!active) return;
    window.addEventListener("keydown", handleCheck);

    return () => {
      window.removeEventListener("keydown", handleCheck);
    };
  }, [active, value]);

  const getRightButton = () => {
    if (edit) {
      return t("common.save");
    }

    if (value.length) {
      return t("common.create");
    }

    return undefined;
  };

  const handleClickRightButton = () => {
    if (edit) {
      handleEditTag();
      return;
    }

    if (value.length) {
      handleCreateTag();
      return;
    }
  };

  const handleEdit = (tag: ITag) => {
    setValue(tag.Name);
    setEdit(tag);
  };

  const handleRemoveTag = (tag: ITag) => {
    onChoose(null);
  };

  useEffect(() => {
    console.log("selected tag", selectedTagId);
    if (selectedTagId) {
      setInternalSelected(allTags.find((x) => x.Id === selectedTagId));
    }
  }, [allTags, selectedTagId]);

  const clear = () => {
    onChoose(null);
    setActive(null);
  };

  const save = () => {
    onChoose(internalSelected);
    setActive(false);
  };

  return (
    <div ref={outsideRef} className={styles.wrapper}>
      <div
        onClick={(e: React.MouseEvent<HTMLDivElement>) => {
          const target = e.target as HTMLElement;
          const ele = target.tagName.toLowerCase();
          const button = target.outerHTML.includes("button");

          if (ele !== "div" || button) {
            return;
          }

          setActive(!active);
          setValue("");
        }}
        className={styles.card}
      >
        <>
          <LabelIcon className={styles.icon} />{" "}
          {label ? label : <>{t("forms.searchTag")}</>}
          {showCounter && selectedTagId && (
            <div className={styles.counter}>1</div>
          )}
          <FontAwesomeIcon
            onClick={() => {
              setActive(!active);
            }}
            className={styles.closeIcon}
            icon={active ? faCaretUp : faCaretDown}
          />
        </>
      </div>

      {active && (
        <DropdownLogic>
          <div className={`${styles.dropdown}`}>
            {allowSearch && (
              <div className={styles.search}>
                <NewInput
                  rightButton={getRightButton()}
                  rightClick={handleClickRightButton}
                  focus
                  tagType={type}
                  labelClassName={styles.x}
                  className={styles.d}
                  value={value}
                  label={edit ? t("tooltips.editTag") : t("forms.searchTag")}
                  onChange={(v) => setValue(v)}
                />
              </div>
            )}
            <div className={styles.choosen}>
              {internalSelected && (
                <TagBadge
                  tag={internalSelected}
                  onRemove={handleRemoveTag}
                ></TagBadge>
              )}
            </div>
            {!edit && (
              <div className={styles.list}>
                {search.map((tag) => (
                  <div
                    key={tag.Id}
                    className={classNames(styles.box)}
                    onClick={() => {
                      setActive(false);
                      onChoose?.(tag);
                      setInternalSelected(tag);
                    }}
                  >
                    <div>{tag.Name}</div>
                    <EditIcon
                      className={styles.icone}
                      onClick={(e) => {
                        handleEdit(tag);
                        e.stopPropagation();
                        e.preventDefault();
                      }}
                    />
                  </div>
                ))}
              </div>
            )}
            {showButtons && (
              <div className={styles.buttons}>
                <HollowButton text={t("common.clear")} onClick={clear}></HollowButton>
                <Button text={t("common.save")} onClick={save}></Button>
              </div>
            )}
          </div>
        </DropdownLogic>
      )}
    </div>
  );
};

export default SearchLabel;
